/**
 * Syncdata handlers that update Redux project store when backend sends
 * project sync messages (app: "project", model: "ksdmproject").
 * - put → update project in state.projects.projects and state.projects.projectsById
 * - post → add new project to both
 * - delete → remove project from both
 */

import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../../app/store';
import {
  updateProjectFromSync,
  addProjectFromSync,
  removeProjectFromSync,
} from '../slices/projectSlice';
import type { SyncdataHandlers, SyncdataMessage } from './syncdataListener';
import type { IProject } from '../models';
import { useSyncdataListener } from '../dataHooks/useSyncdataListener';

const PROJECT_APP = 'project';
const PROJECT_MODEL = 'project'; // frontend model name for ksdmproject

function isProjectMessage(message: SyncdataMessage): boolean {
  return (message.app === PROJECT_APP || message.model === 'ksdmproject') &&
    message.data != null &&
    typeof (message.data as any).id === 'number';
}

/** Returns syncdata handlers that dispatch project slice actions. Use with createSyncdataListener or useSyncdataListener. */
export function createProjectSyncdataHandlers(dispatch: AppDispatch): SyncdataHandlers {
  return {
    onPut(modelName: string, message: SyncdataMessage) {
      if (modelName !== PROJECT_MODEL || !isProjectMessage(message)) return;
      dispatch(updateProjectFromSync(message.data as unknown as IProject));
    },
    onPost(modelName: string, message: SyncdataMessage) {
      if (modelName !== PROJECT_MODEL || !isProjectMessage(message)) return;
      dispatch(addProjectFromSync(message.data as unknown as IProject));
    },
    onDelete(modelName: string, message: SyncdataMessage) {
      if (modelName !== PROJECT_MODEL || !isProjectMessage(message)) return;
      const id = (message.data as { id: number }).id ?? (message as any).id;
      if (id != null) dispatch(removeProjectFromSync(id));
    },
  };
}

/** Hook: subscribe to project syncdata and update Redux. Call once in a layout or provider. */
export function useProjectSyncdata(): void {
  const dispatch = useDispatch<AppDispatch>();
  useSyncdataListener(createProjectSyncdataHandlers(dispatch));
}